'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useTranslations, useLocale, useMessages } from 'next-intl';
import { Loader2, AlertCircle, CheckCircle2, ArrowRight, Mail } from 'lucide-react';
import PasswordInput from '@/app/components/PasswordInput';
import { readQuickHookIntent, clearQuickHookIntent } from '@/lib/quickhook-intent';
import { supabase } from '@/lib/supabase';

const SYMBOL_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export default function RegisterPage() {
  const locale = useLocale();
  const t = useTranslations('Auth.register');
  const tGeneral = useTranslations('General');
  const tLandingFooter = useTranslations('Landing.footer');
  const messages = useMessages();
  const registerMessages = messages?.Auth?.register || {};

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [emailSent, setEmailSent] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptsTerms: false,
    confirmsAdultAge: false,
  });
  const [quickHookIntent, setQuickHookIntent] = useState(null);
  const [practiceInviteToken, setPracticeInviteToken] = useState('');

  useEffect(() => {
    setQuickHookIntent(readQuickHookIntent());
    try {
      const invite = new URLSearchParams(window.location.search).get('invite') || '';
      setPracticeInviteToken(invite.trim());
    } catch {
      setPracticeInviteToken('');
    }
  }, []);

  const optional = (key) => {
    const value = registerMessages[key];
    return typeof value === 'string' ? value : null;
  };

  // Prefer Auth.register keys so we never fall back to English General/Landing leftovers.
  const nameLabel =
    optional('nameLabel') || optional('firstNameLabel') || tGeneral('name');
  const namePlaceholder =
    optional('namePlaceholder') ||
    optional('firstNamePlaceholder') ||
    tGeneral('name');
  const termsLink = optional('termsLink') || tLandingFooter('terms');
  const privacyLink = optional('privacyLink') || tLandingFooter('privacy');

  const mapRegisterErrorCode = (errorCode) => {
    switch (errorCode) {
      case 'PASSWORD_TOO_SHORT':
        return t('errorPasswordLength');
      case 'PASSWORD_MISSING_UPPERCASE':
        return t('errorPasswordUppercase');
      case 'PASSWORD_MISSING_NUMBER':
        return t('errorPasswordNumber');
      case 'PASSWORD_MISSING_SYMBOL':
        return t('errorPasswordSymbol');
      case 'EMAIL_ALREADY_HAS_ACCOUNT':
      case 'EMAIL_ALREADY_REGISTERED':
        return t('errorEmailAlreadyRegistered');
      default:
        return t('errorRegisterProcess');
    }
  };

  const validatePasswordRules = (password) => {
    if (password.length < 8) return t('errorPasswordLength');
    if (!/[A-Z]/.test(password)) return t('errorPasswordUppercase');
    if (!/[0-9]/.test(password)) return t('errorPasswordNumber');
    if (!SYMBOL_RE.test(password)) return t('errorPasswordSymbol');
    return null;
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    setEmailSent(null);

    const passwordError = validatePasswordRules(formData.password);
    if (passwordError) {
      setErrorMsg(passwordError);
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMsg(t('errorPasswordMismatch'));
      setLoading(false);
      return;
    }

    if (!formData.acceptsTerms) {
      setErrorMsg(t('errorTerms'));
      setLoading(false);
      return;
    }

    if (!formData.confirmsAdultAge) {
      setErrorMsg(t('errorAge'));
      setLoading(false);
      return;
    }

    try {
      // Always read latest intent at submit (user may have finished QuickHook in this tab).
      const latestIntent = readQuickHookIntent() || quickHookIntent || null;
      if (latestIntent && !quickHookIntent) setQuickHookIntent(latestIntent);

      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          correo: formData.email,
          nombre: formData.name,
          password: formData.password,
          idioma_preferido: locale,
          tipo_usuario: 'paciente',
          acepta_terminos: formData.acceptsTerms,
          mayor_edad: formData.confirmsAdultAge,
          intent: latestIntent || undefined,
          practice_invite_token: practiceInviteToken || undefined,
        }),
      });

      const contentType = response.headers.get('content-type') || '';
      let result;

      if (contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const raw = await response.text();
        setErrorMsg(
          t('errorRegisterNonJson', { status: response.status, details: raw.slice(0, 140) })
        );
        return;
      }

      if (!response.ok || !result.success) {
        setErrorMsg(
          mapRegisterErrorCode(result?.error_code) || result?.error || t('errorRegisterProcess')
        );
        return;
      }

      const sent = result.verification_email_sent === true;
      setEmailSent(sent);
      setRegisteredEmail(formData.email);
      setSuccessMsg(
        sent ? t('successEmailSent', { email: formData.email }) : t('successEmailFailed')
      );
      clearQuickHookIntent();
      // Never leave a previous account session active after creating a new one.
      await supabase.auth.signOut().catch(() => null);
    } catch (error) {
      console.error('❌ Error:', error);
      let displayError = error?.message || t('errorRegisterProcess');
      if (typeof displayError === 'string' && displayError.includes('already registered')) {
        displayError = t('errorEmailAlreadyRegistered');
      }
      setErrorMsg(displayError);
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch =
    formData.confirmPassword.length === 0 || formData.password === formData.confirmPassword;

  return (
    <div className="min-h-screen bg-[#020617] font-sans selection:bg-cyan-500/30 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-lg">
        <div className="mb-4">
          <Link
            href={`/${locale}`}
            className="text-sm font-semibold text-slate-400 hover:text-cyan-300 transition-colors"
          >
            {t('backHome')}
          </Link>
        </div>

        <div className="rounded-3xl border border-slate-700 bg-slate-950/80 p-6 sm:p-8 shadow-xl">
          <div className="mb-8">
            <p className="text-sm font-bold text-cyan-300 mb-1">biogalfhomehealth.com</p>
            <h1 className="text-3xl font-black text-white mb-2">{t('pageTitle')}</h1>
            <p className="text-base text-slate-300 leading-relaxed">{t('pageSubtitle')}</p>
            {quickHookIntent?.goal || (Array.isArray(quickHookIntent?.goals) && quickHookIntent.goals.length) ? (
              <div className="mt-4 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-3 py-2.5 text-left">
                <p className="text-sm font-bold text-cyan-200">
                  {optional('quickHookHandoffTitle') || 'Tu plan de hoy viene contigo'}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-cyan-100/80">
                  {optional('quickHookHandoffBody') ||
                    'Al crear la cuenta guardamos tus respuestas del plan de hoy para no empezar de cero.'}
                </p>
              </div>
            ) : null}
          </div>

          {errorMsg && (
            <div className="mb-6 p-4 bg-red-950/50 border border-red-500/30 text-red-300 text-base rounded-xl flex items-start gap-3">
              <AlertCircle size={20} className="mt-0.5 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-950/50 text-emerald-400 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-5">
                {emailSent === false ? <Mail size={32} /> : <CheckCircle2 size={32} />}
              </div>
              <h2 className="text-2xl font-black text-white mb-3">{t('successTitle')}</h2>
              <p className="text-base text-slate-300 mb-5 leading-relaxed">{successMsg}</p>
              <div className="bg-slate-900/70 p-4 rounded-xl text-base text-slate-200 mb-6 text-left border border-slate-700">
                <strong className="text-white block mb-1">{t('successNote')}</strong>
                {t('successNoteText')}
                {registeredEmail ? (
                  <span className="block mt-2 text-cyan-300 font-medium break-all">
                    {registeredEmail}
                  </span>
                ) : null}
              </div>
              <div className="space-y-3">
                <Link
                  href={
                    registeredEmail
                      ? `/${locale}/verify?email=${encodeURIComponent(registeredEmail)}`
                      : `/${locale}/verify`
                  }
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-600 px-6 py-3.5 text-base font-bold text-white hover:bg-cyan-500"
                >
                  {t('goToVerify')} <ArrowRight size={18} />
                </Link>
                <Link
                  href={`/${locale}/login`}
                  className="inline-flex w-full items-center justify-center rounded-xl border border-slate-600 px-6 py-3 text-base font-bold text-slate-200 hover:border-cyan-400"
                >
                  {t('goToLogin')}
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleRegister} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-base font-bold text-white">{nameLabel}</label>
                <p className="text-sm text-slate-400">{t('nameHint')}</p>
                <input
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-600 bg-[#0F172A] outline-none text-white text-base placeholder-slate-500 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/40"
                  placeholder={namePlaceholder}
                  onChange={handleChange}
                  autoComplete="name"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-base font-bold text-white">{t('emailLabel')}</label>
                <p className="text-sm text-slate-400">{t('emailHint')}</p>
                <input
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-600 bg-[#0F172A] outline-none text-white text-base placeholder-slate-500 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/40"
                  placeholder={t('emailPlaceholder')}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </div>

              <div className="rounded-xl border border-slate-600 bg-slate-900/50 p-4 space-y-2">
                <p className="text-base font-bold text-white">{t('passwordHelpTitle')}</p>
                <p className="text-sm text-slate-300 leading-relaxed">{t('passwordHelpIntro')}</p>
                <ul className="text-sm text-slate-300 space-y-1 list-disc pl-5">
                  <li>{t('passwordRule1')}</li>
                  <li>{t('passwordRule2')}</li>
                  <li>{t('passwordRule3')}</li>
                  <li>{t('passwordRule4')}</li>
                </ul>
                <p className="text-sm font-bold text-emerald-300">{t('passwordExample')}</p>
              </div>

              <PasswordInput
                label={t('passwordLabel')}
                placeholder={t('passwordPlaceholder')}
                required
                value={formData.password}
                onChange={(e) =>
                  setFormData((current) => ({
                    ...current,
                    password: e.target.value,
                  }))
                }
                autoComplete="new-password"
                showStrengthIndicator
              />

              <div className="space-y-1.5">
                <PasswordInput
                  label={t('confirmPasswordLabel')}
                  placeholder={t('confirmPasswordPlaceholder')}
                  required
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData((current) => ({
                      ...current,
                      confirmPassword: e.target.value,
                    }))
                  }
                  autoComplete="new-password"
                  showStrengthIndicator={false}
                  error={
                    formData.confirmPassword && !passwordsMatch
                      ? t('errorPasswordMismatch')
                      : null
                  }
                />
                <p className="text-sm text-slate-400">{t('confirmPasswordHint')}</p>
              </div>

              <div className="space-y-3 rounded-xl border border-slate-700 bg-slate-900/40 p-4">
                <label
                  htmlFor="acceptsTerms"
                  className="flex items-start gap-3 text-base text-slate-200 cursor-pointer"
                >
                  <input
                    id="acceptsTerms"
                    name="acceptsTerms"
                    type="checkbox"
                    checked={formData.acceptsTerms}
                    onChange={handleChange}
                    className="mt-1 h-5 w-5 rounded border-slate-600 bg-slate-900 text-cyan-500"
                    required
                  />
                  <span>
                    {t('termsCheckbox')}{' '}
                    <Link
                      href={`/${locale}/legal/terminos`}
                      className="font-bold text-cyan-400 hover:text-cyan-300"
                    >
                      {termsLink}
                    </Link>
                    {' · '}
                    <Link
                      href={`/${locale}/legal/privacidad`}
                      className="font-bold text-cyan-400 hover:text-cyan-300"
                    >
                      {privacyLink}
                    </Link>
                  </span>
                </label>

                <label
                  htmlFor="confirmsAdultAge"
                  className="flex items-start gap-3 text-base text-slate-200 cursor-pointer"
                >
                  <input
                    id="confirmsAdultAge"
                    name="confirmsAdultAge"
                    type="checkbox"
                    checked={formData.confirmsAdultAge}
                    onChange={handleChange}
                    className="mt-1 h-5 w-5 rounded border-slate-600 bg-slate-900 text-cyan-500"
                    required
                  />
                  <span>{t('ageCheckbox')}</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={
                  loading ||
                  !formData.password ||
                  !formData.confirmPassword ||
                  !passwordsMatch ||
                  !formData.email ||
                  !formData.name ||
                  !formData.acceptsTerms ||
                  !formData.confirmsAdultAge
                }
                className="w-full flex justify-center items-center gap-2 py-4 px-6 rounded-xl text-base font-bold text-white bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="animate-spin w-5 h-5" /> : t('submitButton')}
              </button>
            </form>
          )}

          <div className="text-center mt-8 pt-6 border-t border-slate-800">
            <p className="text-base text-slate-400">
              {t('alreadyHaveAccount')}{' '}
              <Link
                href={`/${locale}/login`}
                className="font-bold text-cyan-400 hover:text-cyan-300"
              >
                {t('loginLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
